import React from 'react';
import {
  Alert,
  Box,
  Button,
  Checkbox,
  DialogActions,
  DialogContent,
  FormControlLabel,
  Stack,
  TextField,
  Typography
} from '@mui/material';

import CheckIcon from '@mui/icons-material/Check';

import {
  RevokeBureauConsentDialog,
  RevokeBureauConsentSuccessDialog,
  RevokeBureauConsentWrapper
} from './revoke-bureau-consent.styled';
import { useRevokeBureauConsentActions } from './useRevokeBureauConsentActions';

export default function RevokeBureauConsent() {
  const {
    submitError,
    dialogOpen,
    successDialogOpen,
    submitting,
    name,
    phone,
    consentChecked,
    canSubmit,
    handleCloseDialog,
    handleCloseSuccessDialog,
    setName,
    handlePhoneChange,
    setSubmitError,
    setDialogOpen,
    setConsentChecked,
    handleSubmit
  } = useRevokeBureauConsentActions();

  return (
    <RevokeBureauConsentWrapper>
      <div className="rbc-inner">
        <Typography component="h1" variant="h4" className="rbc-page-title">
          Account & consent
        </Typography>
        <Typography variant="body2" className="rbc-subtitle">
          Manage how your identity data is used for bureau checks.
        </Typography>

        <Box
          className="rbc-action-card"
          role="button"
          tabIndex={0}
          aria-label="Revoke Bureau Consent"
          onClick={() => setDialogOpen(true)}
          onKeyDown={e => {
            if (e.key === 'Enter' || e.key === ' ') {
              e.preventDefault();
              setDialogOpen(true);
            }
          }}
        >
          <Typography
            component="span"
            variant="body1"
            className="rbc-action-card-text"
          >
            Revoke Bureau Consent
          </Typography>
        </Box>
      </div>

      <RevokeBureauConsentDialog
        open={dialogOpen}
        onClose={() => {
          if (submitting) return;
          handleCloseDialog();
        }}
        maxWidth="sm"
        fullWidth
        scroll="body"
      >
        <DialogContent className="rbc-dialog-content">
          <Typography component="h2" variant="h6" className="rbc-dialog-title">
            Revoke Bureau Consent
          </Typography>
          <Typography variant="body2" className="rbc-dialog-description">
            Enter your details to submit a bureau consent revocation request.
          </Typography>

          {submitError ? (
            <Alert
              severity="error"
              className="rbc-alert"
              onClose={() => setSubmitError(null)}
            >
              {submitError}
            </Alert>
          ) : null}

          <Stack className="rbc-stack">
            <Box>
              <Typography
                component="label"
                htmlFor="revoke-bureau-name"
                variant="subtitle2"
                className="rbc-field-label rbc-field-label--name"
              >
                Name
              </Typography>
              <TextField
                id="revoke-bureau-name"
                name="name"
                value={name}
                onChange={e => setName(e.target.value)}
                fullWidth
                size="small"
                placeholder=""
                variant="outlined"
                inputProps={{ 'aria-label': 'Name' }}
              />
            </Box>

            <Box>
              <Typography
                component="label"
                htmlFor="revoke-bureau-phone"
                variant="subtitle2"
                className="rbc-field-label rbc-field-label--phone"
              >
                Registered Khatabook phone number
              </Typography>
              <Typography variant="caption" className="rbc-field-hint">
                Use the same 10-digit mobile number as on your Khatabook
                account.
              </Typography>
              <TextField
                id="revoke-bureau-phone"
                name="phone"
                value={phone}
                onChange={handlePhoneChange}
                fullWidth
                size="small"
                placeholder=""
                variant="outlined"
                inputProps={{
                  inputMode: 'numeric',
                  maxLength: 10,
                  'aria-label': 'Registered Khatabook phone number'
                }}
              />
            </Box>

            <FormControlLabel
              className="rbc-consent-row"
              control={
                <Checkbox
                  checked={consentChecked}
                  onChange={e => setConsentChecked(e.target.checked)}
                  color="primary"
                />
              }
              label="I want to revoke my bureau consent."
            />
          </Stack>
        </DialogContent>

        <DialogActions className="rbc-dialog-actions">
          <Button
            variant="outlined"
            className="rbc-btn-cancel"
            disabled={submitting}
            onClick={handleCloseDialog}
          >
            Cancel
          </Button>
          <Button
            variant="contained"
            className="rbc-btn-submit"
            disabled={!canSubmit || submitting}
            onClick={handleSubmit}
          >
            {submitting ? 'Submitting…' : 'Submit'}
          </Button>
        </DialogActions>
      </RevokeBureauConsentDialog>

      <RevokeBureauConsentSuccessDialog
        open={successDialogOpen}
        onClose={handleCloseSuccessDialog}
        maxWidth="xs"
        fullWidth
        scroll="body"
      >
        <DialogContent className="rbc-success-content">
          <div className="rbc-success-icon-wrap" aria-hidden>
            <CheckIcon className="rbc-success-check" />
          </div>
          <Typography component="p" variant="h5" className="rbc-success-title">
            Request received
          </Typography>
          <Typography variant="body2" className="rbc-success-description">
            Your request has been successfully captured. Our team will process
            it, contact you if required, and keep you updated.
          </Typography>
          <Button
            variant="contained"
            className="rbc-success-close-btn"
            disableElevation
            onClick={handleCloseSuccessDialog}
          >
            Close
          </Button>
        </DialogContent>
      </RevokeBureauConsentSuccessDialog>
    </RevokeBureauConsentWrapper>
  );
}
